import { MAX_SCORE, type Format, type SubmitMatchInput } from '@foosball/shared';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { ApiError } from '../api/client';
import { Button, Card, FormError } from '../components/ui';
import { useChallenges } from '../hooks/useChallenges';
import { useActiveSeason, usePlayers, useSubmitMatch } from '../hooks/useMatches';
import { useAuth } from '../lib/auth-context';

function ScoreStepper({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (next: number) => void;
}) {
  return (
    <div className="flex flex-col items-center gap-2">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </span>
      <div className="flex items-center gap-3">
        <button
          type="button"
          aria-label={`Decrease ${label}`}
          onClick={() => onChange(Math.max(0, value - 1))}
          className="size-11 rounded-full border border-slate-300 text-xl font-bold text-slate-600 active:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:active:bg-slate-800"
        >
          −
        </button>
        <output className="w-12 text-center text-3xl font-bold tabular-nums">{value}</output>
        <button
          type="button"
          aria-label={`Increase ${label}`}
          onClick={() => onChange(Math.min(MAX_SCORE, value + 1))}
          className="size-11 rounded-full border border-slate-300 text-xl font-bold text-slate-600 active:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:active:bg-slate-800"
        >
          +
        </button>
      </div>
    </div>
  );
}

function PlayerSelect({
  label,
  value,
  onChange,
  options,
  disabledIds,
}: {
  label: string;
  value: number | null;
  onChange: (id: number | null) => void;
  options: { id: number; displayName: string }[];
  disabledIds: number[];
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 dark:text-slate-200">{label}</span>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-base dark:border-slate-700 dark:bg-slate-950"
      >
        <option value="">Choose a player…</option>
        {options.map((player) => (
          <option key={player.id} value={player.id} disabled={disabledIds.includes(player.id)}>
            {player.displayName}
          </option>
        ))}
      </select>
    </label>
  );
}

export function SubmitMatchPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const players = usePlayers();
  const season = useActiveSeason();
  const submit = useSubmitMatch();

  // Arriving from "Enter the result" on a scheduled challenge. The id is
  // carried through to the API so the challenge is closed by the same write
  // that records the match; the opponent is read from the challenge rather
  // than trusted from the URL, so a hand-edited link cannot mislabel a game.
  const challengeIdParam = Number(searchParams.get('challenge'));
  const challengeId = Number.isInteger(challengeIdParam) && challengeIdParam > 0
    ? challengeIdParam
    : null;

  const challenges = useChallenges();
  const fromChallenge =
    challengeId === null
      ? undefined
      : challenges.data?.scheduled.find((c) => c.id === challengeId);

  const [format, setFormat] = useState<Format>('1v1');
  const [partner, setPartner] = useState<number | null>(null);
  const [opponent1, setOpponent1] = useState<number | null>(null);
  const [opponent2, setOpponent2] = useState<number | null>(null);
  const [scoreA, setScoreA] = useState(10);
  const [scoreB, setScoreB] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const others = (players.data ?? []).filter((p) => p.id !== user?.id);
  const chosen = [partner, opponent1, opponent2].filter((id): id is number => id !== null);

  // With a challenge in hand the opponent is fixed, so the picker is replaced
  // by a plain statement of who you agreed to play.
  const lockedOpponent =
    fromChallenge === undefined
      ? null
      : fromChallenge.challenger.id === user?.id
        ? fromChallenge.opponent
        : fromChallenge.challenger;
  const effectiveFormat = fromChallenge?.format ?? format;

  // Only send the id if the challenge really is still awaiting a result. If it
  // has since been withdrawn or settled elsewhere, sending it anyway would make
  // the API reject the whole submission — which would contradict the notice
  // above telling the player they can still record the game.
  const settleChallengeId = fromChallenge === undefined ? null : challengeId;

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    if (!user) return;

    const settledOpponent = lockedOpponent?.id ?? opponent1;
    const needed =
      effectiveFormat === '1v1' ? [settledOpponent] : [partner, settledOpponent, opponent2];
    if (needed.some((id) => id === null)) {
      setError('Choose every player first.');
      return;
    }
    if (scoreA === scoreB) {
      setError('Foosball has no draws — the scores must differ.');
      return;
    }

    const payload: SubmitMatchInput = {
      format: effectiveFormat,
      scoreA,
      scoreB,
      players:
        effectiveFormat === '1v1'
          ? [
              { userId: user.id, side: 'A' },
              { userId: settledOpponent!, side: 'B' },
            ]
          : [
              { userId: user.id, side: 'A' },
              { userId: partner!, side: 'A' },
              { userId: settledOpponent!, side: 'B' },
              { userId: opponent2!, side: 'B' },
            ],
      ...(settleChallengeId === null ? {} : { challengeId: settleChallengeId }),
    };

    try {
      await submit.mutateAsync(payload);
      void navigate(settleChallengeId === null ? '/matches' : '/challenges');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not save that result.');
    }
  }

  if (season.data === null) {
    return (
      <Card>
        <h1 className="text-lg font-semibold">No active season</h1>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          An administrator needs to start a season before results can be recorded.
        </p>
      </Card>
    );
  }

  return (
    <>
      <h1 className="mb-1 text-2xl font-bold tracking-tight">
        {lockedOpponent ? 'Result of your challenge' : 'Submit a result'}
      </h1>
      <p className="mb-5 text-sm text-slate-500 dark:text-slate-400">
        {season.data ? `${season.data.name} — first to ${season.data.winScore}` : 'Loading season…'}
      </p>

      {challengeId !== null && !challenges.isPending && fromChallenge === undefined && (
        <div className="mb-5">
          <FormError message="That challenge is no longer waiting for a result. You can still record the game below." />
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {lockedOpponent === null && (
        <Card>
          <div
            role="group"
            aria-label="Match format"
            className="grid grid-cols-2 gap-2 rounded-lg bg-slate-100 p-1 dark:bg-slate-800"
          >
            {(['1v1', '2v2'] as const).map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={format === option}
                onClick={() => setFormat(option)}
                className={`rounded-md py-2 text-sm font-semibold transition ${
                  format === option
                    ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-950 dark:text-slate-100'
                    : 'text-slate-500 dark:text-slate-400'
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        </Card>
        )}

        <Card className="space-y-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Your side
          </h2>
          <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm dark:bg-slate-800">
            {user?.displayName} <span className="text-slate-400">(you)</span>
          </p>
          {effectiveFormat === '2v2' && (
            <PlayerSelect
              label="Your partner"
              value={partner}
              onChange={setPartner}
              options={others}
              disabledIds={chosen.filter((id) => id !== partner)}
            />
          )}
        </Card>

        <Card className="space-y-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {lockedOpponent ? 'Opponent' : 'Opponents'}
          </h2>
          {lockedOpponent ? (
            <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm dark:bg-slate-800">
              {lockedOpponent.displayName}{' '}
              <span className="text-slate-400">(the player you challenged)</span>
            </p>
          ) : (
            <PlayerSelect
              label={effectiveFormat === '1v1' ? 'Opponent' : 'Opponent 1'}
              value={opponent1}
              onChange={setOpponent1}
              options={others}
              disabledIds={chosen.filter((id) => id !== opponent1)}
            />
          )}
          {effectiveFormat === '2v2' && (
            <PlayerSelect
              label="Opponent 2"
              value={opponent2}
              onChange={setOpponent2}
              options={others}
              disabledIds={chosen.filter((id) => id !== opponent2)}
            />
          )}
        </Card>

        <Card>
          <div className="flex items-start justify-around">
            <ScoreStepper label="Your score" value={scoreA} onChange={setScoreA} />
            <span className="pt-8 text-2xl font-light text-slate-300">–</span>
            <ScoreStepper label="Their score" value={scoreB} onChange={setScoreB} />
          </div>
        </Card>

        <FormError message={error} />

        <Button type="submit" disabled={submit.isPending}>
          {submit.isPending ? 'Saving…' : 'Submit result'}
        </Button>

        <p className="pb-4 text-center text-xs text-slate-400 dark:text-slate-500">
          An opponent must confirm before ratings change.
        </p>
      </form>
    </>
  );
}
